/**
 * Glass-styled modal dialog for the learner experience.
 * Wraps React Aria Dialog for accessibility, focus trapping, and keyboard dismiss.
 *
 * Visual: glass surface with blur backdrop, soft shadow, top-right close button.
 * Uses the learner semantic token contract.
 */
import { type ReactNode, useSyncExternalStore } from 'react';
import {
  Dialog as AriaDialog,
  Modal,
  ModalOverlay,
  Heading,
  Button as AriaButton,
} from 'react-aria-components';
import { X } from 'lucide-react';
import { useInterfacePortalContainer } from '@/foundation/ui/InterfacePortalContext';
import { LEARNER_LAYOUT_MEDIA } from '../theme/layout';

// ─── Animation keyframes (injected once) ─────────────────────────────────────

// Overlay must outlast the dialog animation — React Aria sets isEntering=false
// when overlay animations finish. Reaches full opacity at 30%, then holds.
const OVERLAY_IN = 'fl-overlay-in 550ms cubic-bezier(0.16, 1, 0.3, 1) forwards';
const OVERLAY_OUT = 'fl-overlay-out 350ms cubic-bezier(0.16, 1, 0.3, 1) forwards';
// Enter animation lives on AriaDialog (same element as backdrop-filter) so the
// browser pre-composites the blur even at opacity 0.01.
const DIALOG_IN = 'fl-dialog-in 450ms cubic-bezier(0.16, 1, 0.3, 1) both';
const DIALOG_OUT = 'fl-dialog-out 300ms cubic-bezier(0.16, 1, 0.3, 1) forwards';

const MOBILE_MQL =
  typeof window !== 'undefined' ? window.matchMedia(LEARNER_LAYOUT_MEDIA.maxMobile) : null;

function useIsMobile(): boolean {
  return useSyncExternalStore(
    (cb) => {
      if (!MOBILE_MQL) return () => {};
      MOBILE_MQL.addEventListener('change', cb);
      return () => MOBILE_MQL.removeEventListener('change', cb);
    },
    () => MOBILE_MQL?.matches ?? false,
    () => false
  );
}

const KEYFRAMES = `
@keyframes fl-overlay-in { 0% { opacity: 0 } 30% { opacity: 1 } 100% { opacity: 1 } }
@keyframes fl-overlay-out { from { opacity: 1 } to { opacity: 0 } }
@keyframes fl-dialog-in { from { opacity: 0.01; transform: scale(0.97) translateY(6px) } to { opacity: 1; transform: scale(1) translateY(0) } }
@keyframes fl-dialog-out { from { opacity: 1; transform: scale(1) translateY(0) } to { opacity: 0; transform: scale(0.97) translateY(6px) } }
`;

// ─── Size map ────────────────────────────────────────────────────────────────

const SIZE_MAP: Record<string, number> = {
  sm: 400,
  md: 480,
  lg: 560,
  xl: 640,
};

// ─── Types ───────────────────────────────────────────────────────────────────

export interface DialogProps {
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Called when the dialog should close (backdrop click, Escape, close button) */
  onClose: () => void;
  /** Dialog title shown in the header */
  title: string;
  /** Dialog content */
  children: ReactNode;
  /** Max-width preset (default: lg) */
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

// ─── Component ───────────────────────────────────────────────────────────────

export function Dialog({ isOpen, onClose, title, children, size = 'lg' }: DialogProps) {
  const isMobile = useIsMobile();
  const portalContainer = useInterfacePortalContainer();

  return (
    <>
      <style>{KEYFRAMES}</style>
      <ModalOverlay
        isOpen={isOpen}
        UNSTABLE_portalContainer={portalContainer ?? undefined}
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
        isDismissable
        className="fixed inset-0 z-50 flex justify-center"
        style={({ isEntering, isExiting }) => ({
          alignItems: isMobile ? 'flex-start' : 'center',
          padding: isMobile ? 0 : 16,
          background: isMobile ? 'transparent' : 'rgba(0, 0, 0, 0.25)',
          backdropFilter: isMobile ? 'none' : 'blur(6px) saturate(1.2)',
          WebkitBackdropFilter: isMobile ? 'none' : 'blur(6px) saturate(1.2)',
          animation: isEntering ? OVERLAY_IN : isExiting ? OVERLAY_OUT : undefined,
          opacity: isEntering ? 0 : undefined,
        })}
      >
        <Modal
          className="w-full outline-none"
          style={({ isExiting }) => ({
            maxWidth: isMobile ? '100%' : SIZE_MAP[size],
            height: isMobile ? '100dvh' : undefined,
            animation: isExiting ? DIALOG_OUT : undefined,
          })}
        >
          <AriaDialog
            className="outline-none"
            style={{
              borderRadius: isMobile ? 0 : 14,
              background: isMobile ? 'var(--color-bg)' : 'var(--color-surface)',
              backdropFilter: isMobile ? 'none' : 'blur(16px) saturate(1.3)',
              WebkitBackdropFilter: isMobile ? 'none' : 'blur(16px) saturate(1.3)',
              border: isMobile ? 'none' : '1px solid var(--color-surface-border)',
              boxShadow: isMobile
                ? 'none'
                : 'inset 0 0.5px 0 var(--color-surface-highlight), 0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)',
              height: isMobile ? '100%' : undefined,
              maxHeight: isMobile ? '100dvh' : '85vh',
              overflow: 'hidden',
              animation: DIALOG_IN,
            }}
          >
            {/* Header */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '18px 20px 12px',
              }}
            >
              <Heading
                slot="title"
                style={{
                  fontSize: 17,
                  fontWeight: 700,
                  color: 'var(--color-text)',
                  fontFamily: 'var(--font-display)',
                  letterSpacing: '-0.02em',
                }}
              >
                {title}
              </Heading>
              <AriaButton
                onPress={() => onClose()}
                aria-label="Close"
                className="cursor-pointer"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 28,
                  height: 28,
                  borderRadius: 8,
                  background: 'var(--color-surface-muted)',
                  border: 'none',
                  color: 'var(--color-text-muted)',
                  transition: 'background 0.15s',
                }}
              >
                <X size={14} strokeWidth={2.5} />
              </AriaButton>
            </div>

            {/* Body */}
            <div
              style={{
                padding: '0 20px 20px',
                overflowY: 'auto',
                maxHeight: isMobile ? 'calc(100dvh - 60px)' : 'calc(85vh - 60px)',
              }}
            >
              {children}
            </div>
          </AriaDialog>
        </Modal>
      </ModalOverlay>
    </>
  );
}
